import {
  parseCurrentAdminRbacPermissionsResponse,
  type CurrentAdminRbacPermissionGrant,
  type CurrentAdminRbacPermissionsResponse,
  type RbacV2RolePermission,
} from '@gozoki/api-types'

/** Contrat `GET /rbac/me` exploitable, ou `null` : réponse absente, legacy ou mal formée. */
export const parseRbacContract = (value: unknown): CurrentAdminRbacPermissionsResponse | null =>
  parseCurrentAdminRbacPermissionsResponse(value)

/** Réponse exploitable ⇒ le BO peut lui faire confiance, dans un régime comme dans l'autre. */
export const isUsableRbacResponse = (value: unknown): boolean => parseRbacContract(value) !== null

/**
 * - `signed-out` : aucun compte.
 * - `loading` : première réponse attendue, rien à afficher.
 * - `ready` : contrat exploitable frais ; s'il annonce `permissionV2Enabled`, c'est le mode strict.
 * - `stale` : réponse courante inexploitable, mais une réponse exploitable de la session sert
 *   encore de référence — un compte passé en v2 ne retombe jamais sur le régime hérité, qui serait
 *   permissif sur les vérifications v2.
 * - `legacy` : aucun contrat exploitable de toute la session ⇒ régime hérité fondé sur les bits,
 *   exactement comme `staging`. Jamais bloquant.
 */
export type RbacSessionState = 'signed-out' | 'loading' | 'ready' | 'stale' | 'legacy'

export interface RbacSessionResolution {
  state: RbacSessionState
  /** Contrat retenu pour l'ability ; `null` ⇒ régime hérité (bits v1, v2 inerte). */
  contract: CurrentAdminRbacPermissionsResponse | null
  /** Bandeau discret « autorisations avancées indisponibles » + nouvelle tentative en cours. */
  degraded: boolean
}

export interface RbacSessionInput {
  hasUser: boolean
  pending: boolean
  error: boolean
  /** Contrat exploitable de la réponse courante, `null` si absente/en erreur/incompatible. */
  contract: CurrentAdminRbacPermissionsResponse | null
  /** Dernier contrat exploitable obtenu pendant la session. */
  remembered: CurrentAdminRbacPermissionsResponse | null
}

/**
 * Le mode strict ne s'applique que sur une réponse exploitable. Une réponse absente, en erreur ou
 * de forme incompatible — BO déployé deux minutes avant le backend, 502 passager — fait retomber
 * le BO sur le régime hérité au lieu de verrouiller tout le monde : un compte au régime hérité
 * n'est jamais bloqué par cet endpoint.
 */
export const resolveRbacSession = ({
  hasUser,
  pending,
  error,
  contract,
  remembered,
}: RbacSessionInput): RbacSessionResolution => {
  if (!hasUser) return { state: 'signed-out', contract: null, degraded: false }
  if (contract !== null) return { state: 'ready', contract, degraded: false }

  // Repli non permissif : on garde l'ability déjà installée plutôt que de rouvrir la v1.
  if (remembered !== null) return { state: 'stale', contract: remembered, degraded: !pending }

  if (pending && !error) return { state: 'loading', contract: null, degraded: false }

  return { state: 'legacy', contract: null, degraded: true }
}

/**
 * Le flag `permission-v2` du **contrat retenu**, seule source à laquelle l'ability et les sites
 * d'appel doivent se fier. En état `stale`, le contrat mémorisé fait toujours foi : un compte v2 ne
 * retombe pas sur le régime hérité (permissif côté v2, et sans aucun bit pour un compte v2 pur).
 */
export const rbacV2EnabledFromSession = (session: RbacSessionResolution): boolean =>
  session.contract?.permissionV2Enabled ?? false

export interface RbacAbilityInput {
  grants: CurrentAdminRbacPermissionGrant[]
  grantsV2: RbacV2RolePermission[]
  enforcementEnabled: boolean
  permissionV2Enabled: boolean
}

/**
 * Arguments d'`createAppAbility` pour le contrat retenu. Sans contrat, les valeurs sont celles du
 * régime hérité de `staging` : grants v1 vides, `permissionV2Enabled` faux — jamais le v2 strict,
 * qui ne se déclenche que sur une réponse annonçant explicitement le flag actif.
 */
export const rbacAbilityInput = (
  contract: CurrentAdminRbacPermissionsResponse | null,
): RbacAbilityInput => ({
  grants: contract?.grants ?? [],
  grantsV2: contract?.grantsV2 ?? [],
  enforcementEnabled: contract?.enforcementEnabled ?? true,
  permissionV2Enabled: contract?.permissionV2Enabled ?? false,
})
